import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 

function ManageUser() {
  const token = localStorage.getItem("accessToken");

  const [clients, setClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  // Loading & error states
  const [loading, setLoading] = useState(true);
  const [fetchingPermissions, setFetchingPermissions] = useState(false);
  const [error, setError] = useState(null);

  // Permissions state - now directly for clients
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // State for row selection (for deleting permissions)
  const [selectedRowsForDelete, setSelectedRowsForDelete] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [singleDeleteDialogOpen, setSingleDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" // "success" | "error" | "info" | "warning"
  });

  // Memoize headers to prevent unnecessary re-renders
  const headers = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  }), [token]);

  // Fetch all clients
  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError("No access token found. Please login.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const clientRes = await fetch(`${API_BASE_URL}/api/core/GetClients/`, { headers });

        if (!clientRes.ok) {
          throw new Error(`Failed to load clients`);
        }

        const clientData = await clientRes.json();

        // Set clients
        const clientsList = Array.isArray(clientData)
          ? clientData
          : clientData?.data || clientData?.clients || clientData?.results || [];
        
        setClients(clientsList);
        
        // Initialize permissions for all clients with default false values
      const initialPermissions = clientsList.map(client => ({
  id: client.client_id,
  clientName: client.client_name,
  clientCode: client.client_code, // Using client_code from API response
  read: false,
  create: false,
  update: false,
  delete: false,
  is_active: true, // Always true
}));
        
        setPermissions(initialPermissions);
        setOriginalPermissions(structuredClone(initialPermissions));

      } catch (err) {
        console.error("Dropdown fetch error:", err);
        setError(err.message || "Failed to load clients");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [headers, token]);

  // Fetch existing client permissions when selected clients change
  useEffect(() => {
    if (selectedClients.length === 0) {
      // Reset to default permissions (all false) when no client selected
   const resetPermissions = clients.map(client => ({
  id: client.client_id,
  clientName: client.client_name,
  clientCode: client.client_code, // Using client_code from API response
  read: false,
  create: false,
  update: false,
  delete: false,
  is_active: true,
}));
      setPermissions(resetPermissions);
      setOriginalPermissions(structuredClone(resetPermissions));
      setHasChanges(false);
      return;
    }

const fetchClientPermissions = async () => {
  setLoadingPermissions(true);
  setError(null);

  try {
    // Send client_codes as query parameter
    const clientCodes = selectedClients.join(",");
    const res = await fetch(
      `${API_BASE_URL}/api/elearning_settings/client-permissions/bulk/?client_codes=${clientCodes}`,
      { headers }
    );

    if (!res.ok) {
      throw new Error("Failed to load client permissions");
    }

    const data = await res.json();

    // Update permissions based on API response
    let updatedPermissions = permissions.map((perm) => {
      // Find matching permission from API
      const apiPerm = Array.isArray(data) 
        ? data.find(p => p.client_code === perm.clientCode)
        : null;

      return {
        ...perm,
        read: apiPerm?.can_read ?? perm.read,
        create: apiPerm?.can_create ?? perm.create,
        update: apiPerm?.can_update ?? perm.update,
        delete: apiPerm?.can_delete ?? perm.delete,
        is_active: true,
      };
    });

    setPermissions(updatedPermissions);
    setOriginalPermissions(structuredClone(updatedPermissions));
    setHasChanges(false);
    setSelectedRowsForDelete([]);

  } catch (err) {
    console.error("Fetch permissions error:", err);
    setError("Failed to load permissions for selected clients");
  } finally {
    setLoadingPermissions(false);
  }
};

    if (selectedClients.length > 0) {
      fetchClientPermissions();
    }
  }, [selectedClients, clients, headers]);

  // Toggle entire column for selected clients only
 const toggleColumn = (field) => {
  const selectedClientCodes = new Set(selectedClients);
  
  setPermissions((prev) =>
    prev.map((client) => {
      if (selectedClientCodes.has(client.clientCode)) {
        return { ...client, [field]: !client[field] };
      }
      return client;
    })
  );
  setHasChanges(true);
};

  const togglePermission = (clientCode, field) => {
    setPermissions((prev) =>
      prev.map((client) =>
        client.clientCode === clientCode ? { ...client, [field]: !client[field] } : client
      )
    );
    setHasChanges(true);
  };

  // Handle row selection for deletion
  const toggleRowSelection = (clientCode) => {
    setSelectedRowsForDelete((prev) => {
      if (prev.includes(clientCode)) {
        return prev.filter(code => code !== clientCode);
      } else {
        return [...prev, clientCode];
      }
    });
  };

  const toggleSelectAllRows = () => {
    if (selectedRowsForDelete.length === displayedClients.length) {
      setSelectedRowsForDelete([]);
    } else {
      setSelectedRowsForDelete(displayedClients.map(client => client.clientCode));
    }
  };

  // Open single delete confirmation modal
  const openSingleDeleteDialog = (clientCode, clientName) => {
    setClientToDelete({ clientCode, clientName });
    setSingleDeleteDialogOpen(true);
  };

  // Delete single client permission
  const handleDeleteSingle = async () => {
    if (!clientToDelete) return;

    setSingleDeleteDialogOpen(false);
    setFetchingPermissions(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/elearning_settings/client-permissions/${clientToDelete.clientCode}/delete/`,
        { 
          method: "DELETE",
          headers 
        }
      );

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(`Delete failed: ${response.status} - ${errorText}`);
      }

      // Reset permissions for this client to false
      setPermissions((prev) =>
        prev.map((client) =>
          client.clientCode === clientToDelete.clientCode
            ? { ...client, read: false, create: false, update: false, delete: false }
            : client
        )
      );

      setOriginalPermissions((prev) =>
        prev.map((client) =>
          client.clientCode === clientToDelete.clientCode
            ? { ...client, read: false, create: false, update: false, delete: false }
            : client
        )
      );

      // Show success message
      setSnackbar({
        open: true,
        message: `Permissions deleted successfully for ${clientToDelete.clientName}`,
        severity: "success"
      });
      
    } catch (err) {
      console.error("Delete error:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete client permissions",
        severity: "error"
      });
    } finally {
      setFetchingPermissions(false);
      setClientToDelete(null);
    }
  };

// Delete multiple clients permissions (bulk)
const handleDeleteMultiple = async () => {
  if (selectedRowsForDelete.length === 0) {
    setSnackbar({
      open: true,
      message: "Please select at least one client to delete",
      severity: "warning"
    });
    return;
  }

  setDeleteDialogOpen(false);
  setFetchingPermissions(true);
  setError(null);

  try {
    const payload = {
      client_codes: selectedRowsForDelete // Array of client codes
    };

    const response = await fetch(
      `${API_BASE_URL}/api/elearning_settings/client-permissions/bulk/`,
      { 
        method: "DELETE",
        headers,
        body: JSON.stringify(payload)
      }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      throw new Error(`Bulk delete failed: ${response.status} - ${errorText}`);
    }

    // Reset permissions for deleted clients to false
    setPermissions((prev) =>
      prev.map((client) =>
        selectedRowsForDelete.includes(client.clientCode)
          ? { ...client, read: false, create: false, update: false, delete: false }
          : client
      )
    );

    setOriginalPermissions((prev) =>
      prev.map((client) =>
        selectedRowsForDelete.includes(client.clientCode)
          ? { ...client, read: false, create: false, update: false, delete: false }
          : client
      )
    );

    // Show success message
    setSnackbar({
      open: true,
      message: `Permissions deleted successfully for ${selectedRowsForDelete.length} client(s)`,
      severity: "success"
    });
    
    setSelectedRowsForDelete([]);
    
  } catch (err) {
    console.error("Bulk delete error:", err);
    setSnackbar({
      open: true,
      message: err.message || "Failed to delete client permissions",
      severity: "error"
    });
  } finally {
    setFetchingPermissions(false);
  }
};

  const handleSave = async () => {
    if (selectedClients.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select at least one Client",
        severity: "warning"
      });
      return;
    }

    setFetchingPermissions(true);
    setError(null);

    try {
      // Prepare payload for selected clients only
      const selectedPermissions = permissions.filter(perm => 
        selectedClients.includes(perm.clientCode)
      );

      const payload = {
        clients: selectedPermissions.map((perm) => ({
          client_code: perm.clientCode,
          can_create: perm.create,
          can_read: perm.read,
          can_update: perm.update,
          can_delete: perm.delete,
          is_active: true, // Always true
        })),
      };

      const endpoint = `${API_BASE_URL}/api/elearning_settings/client-permissions/bulk/`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        throw new Error(`Save failed: ${response.status} - ${errorText}`);
      }

      // Success
      const savedPermissions = structuredClone(permissions);
      setOriginalPermissions(savedPermissions);
      setHasChanges(false);
      
      setSnackbar({
        open: true,
        message: "Client permissions saved successfully!",
        severity: "success"
      });

    } catch (err) {
      console.error("Save error:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to save client permissions",
        severity: "error"
      });
    } finally {
      setFetchingPermissions(false);
    }
  };

  const handleCancel = () => {
    setPermissions(structuredClone(originalPermissions));
    setHasChanges(false);
    setSelectedRowsForDelete([]);
    setSnackbar({
      open: true,
      message: "Changes have been discarded",
      severity: "info"
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Get only selected clients to display
  const displayedClients = useMemo(() => {
    if (selectedClients.length === 0) return [];
    return permissions.filter(perm => selectedClients.includes(perm.clientCode));
  }, [permissions, selectedClients]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Manage Client Permissions
      </Typography>

      {/* Client Selection Dropdown */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-end">
          <FormControl sx={{ minWidth: 280 }} size="small" required>
            <InputLabel>Client(s) *</InputLabel>
           <Select
  multiple
  value={selectedClients}
  label="Client(s) *"
  onChange={(e) => {
    setSelectedClients(e.target.value);
    setSelectedRowsForDelete([]);
  }}
  renderValue={(selected) =>
    selected
      .map((code) => {
        const clientObj = clients.find(
          (c) => c.client_code === code // Find by client_code
        );
        return clientObj?.client_name || code;
      })
      .join(", ")
  }
>
  {clients.map((c) => {
    const clientValue = c.client_code; // Use client_code as value
    return (
      <MenuItem key={c.client_id} value={clientValue}>
        <Checkbox checked={selectedClients.indexOf(clientValue) > -1} />
        <ListItemText 
          primary={c.client_name} 
        />
      </MenuItem>
    );
  })}
</Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Permissions Table */}
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        {loadingPermissions || fetchingPermissions ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {selectedClients.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography color="text.secondary">
                  Please select at least one client to manage permissions
                </Typography>
              </Box>
            ) : (
              <>
                {/* Delete Selected Button */}
                {displayedClients.length > 0 && (
                  <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={selectedRowsForDelete.length === 0 || fetchingPermissions || loadingPermissions}
                    >
                      Delete Selected ({selectedRowsForDelete.length})
                    </Button>
                  </Stack>
                )}

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell>S.No</TableCell>
                        <TableCell align="center" padding="checkbox">
                          <Checkbox
                            checked={
                              displayedClients.length > 0 && 
                              selectedRowsForDelete.length === displayedClients.length
                            }
                            indeterminate={
                              selectedRowsForDelete.length > 0 && 
                              selectedRowsForDelete.length < displayedClients.length
                            }
                            onChange={toggleSelectAllRows}
                          />
                        </TableCell>
                        <TableCell>Client Name</TableCell>
                        <TableCell align="center">
                          Read
                          <Checkbox
                            indeterminate={
                              displayedClients.some(c => c.read) && !displayedClients.every(c => c.read)
                            }
                            checked={displayedClients.length > 0 && displayedClients.every(c => c.read)}
                            onChange={() => toggleColumn("read")}
                          />
                        </TableCell>
                        <TableCell align="center">
                          Create
                          <Checkbox
                            indeterminate={
                              displayedClients.some(c => c.create) && !displayedClients.every(c => c.create)
                            }
                            checked={displayedClients.length > 0 && displayedClients.every(c => c.create)}
                            onChange={() => toggleColumn("create")}
                          />
                        </TableCell>
                        <TableCell align="center">
                          Update
                          <Checkbox
                            indeterminate={
                              displayedClients.some(c => c.update) && !displayedClients.every(c => c.update)
                            }
                            checked={displayedClients.length > 0 && displayedClients.every(c => c.update)}
                            onChange={() => toggleColumn("update")}
                          />
                        </TableCell>
                        <TableCell align="center">
                          Delete
                          <Checkbox
                            indeterminate={
                              displayedClients.some(c => c.delete) && !displayedClients.every(c => c.delete)
                            }
                            checked={displayedClients.length > 0 && displayedClients.every(c => c.delete)}
                            onChange={() => toggleColumn("delete")}
                          />
                        </TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {displayedClients.length > 0 ? (
                        displayedClients.map((client, index) => (
                          <TableRow key={client.id} hover>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell align="center" padding="checkbox">
                              <Checkbox
                                checked={selectedRowsForDelete.includes(client.clientCode)}
                                onChange={() => toggleRowSelection(client.clientCode)}
                              />
                            </TableCell>
                            <TableCell>{client.clientName}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={!!client.read}
                                onChange={() => togglePermission(client.clientCode, "read")}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={!!client.create}
                                onChange={() => togglePermission(client.clientCode, "create")}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={!!client.update}
                                onChange={() => togglePermission(client.clientCode, "update")}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={!!client.delete}
                                onChange={() => togglePermission(client.clientCode, "delete")}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Delete permissions" arrow>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => openSingleDeleteDialog(client.clientCode, client.clientName)}
                                  disabled={fetchingPermissions || loadingPermissions}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                            <Typography color="text.secondary">
                              No clients selected
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {permissions.length > 0 && (
                  <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={!hasChanges || fetchingPermissions || loadingPermissions}
                    >
                      CANCEL
                    </Button>

                    <Tooltip
                      title={
                        !selectedClients.length
                          ? "Please select at least one Client"
                          : !hasChanges
                          ? "No changes made yet"
                          : fetchingPermissions || loadingPermissions
                          ? "Saving..."
                          : ""
                      }
                      arrow
                      placement="top"
                    >
                      <span>
                        <Button
                          variant="contained"
                          onClick={handleSave}
                          disabled={
                            !hasChanges || 
                            fetchingPermissions || 
                            loadingPermissions || 
                            selectedClients.length === 0
                          }
                        >
                          {fetchingPermissions || loadingPermissions ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            "SAVE"
                          )}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                )}

                {/* Bulk Delete Confirmation Dialog */}
                <Dialog
                  open={deleteDialogOpen}
                  onClose={() => setDeleteDialogOpen(false)}
                >
                  <DialogTitle>Confirm Delete</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Are you sure you want to delete permissions for the following {selectedRowsForDelete.length} client(s)?
                      <ul>
                        {selectedRowsForDelete.map(code => {
                          const client = permissions.find(p => p.clientCode === code);
                          return <li key={code}>{client?.clientName || code}</li>;
                        })}
                      </ul>
                      This action cannot be undone.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} data-app-cancel="true">Cancel</Button>
                    <Button onClick={handleDeleteMultiple} color="error" variant="contained">
                      Delete
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Single Delete Confirmation Dialog */}
                <Dialog
                  open={singleDeleteDialogOpen}
                  onClose={() => setSingleDeleteDialogOpen(false)}
                >
                  <DialogTitle>Confirm Delete</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Are you sure you want to delete permissions for "{clientToDelete?.clientName}"?
                      <br />
                      This action cannot be undone.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSingleDeleteDialogOpen(false)} data-app-cancel="true">Cancel</Button>
                    <Button onClick={handleDeleteSingle} color="error" variant="contained">
                      Delete
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                  open={snackbar.open}
                  autoHideDuration={6000}
                  onClose={handleCloseSnackbar}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                  <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                  >
                    {snackbar.message}
                  </Alert>
                </Snackbar>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default ManageUser;